import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workScheduleService, userService } from '../services/api';
import { Calendar, Clock, Plus, Edit, Trash2, Save, X, ChevronDown, ChevronUp, User } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';

const WorkSchedulesManagement = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [expandedUsers, setExpandedUsers] = useState({});
  const [confirmModal, setConfirmModal] = useState({ show: false, onConfirm: null, message: '' });
  const [formData, setFormData] = useState({
    user_id: '',
    selectedDays: [], // Array de dias selecionados
    start_time: '',
    end_time: '',
    break_start_time: '',
    break_end_time: '',
    is_active: true
  });

  // Buscar jornadas de trabalho
  const { data: schedulesData, isLoading } = useQuery({
    queryKey: ['work-schedules', selectedUserId],
    queryFn: () => workScheduleService.getWorkSchedules({ 
      user_id: selectedUserId || undefined,
      is_active: true 
    }),
    staleTime: 30000,
  });

  // Buscar funcionários ativos
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users-work-schedules'],
    queryFn: () => userService.getUsers({ limit: 100, active: true }),
    staleTime: 5 * 60 * 1000,
  });

  // Mutação para criar jornada (múltiplas)
  const createMutation = useMutation({
    mutationFn: async (schedulesData) => {
      // Criar todas as jornadas em paralelo
      const promises = schedulesData.map(data => workScheduleService.createWorkSchedule(data));
      return Promise.all(promises);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['work-schedules']);
      const count = variables.length;
      toast.success(`${count} jornada${count > 1 ? 's' : ''} criada${count > 1 ? 's' : ''} com sucesso!`);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar jornada de trabalho');
    },
  });

  // Mutação para atualizar jornada
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => workScheduleService.updateWorkSchedule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['work-schedules']);
      toast.success('Jornada de trabalho atualizada com sucesso!');
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar jornada de trabalho');
    },
  });

  // Mutação para deletar jornada
  const deleteMutation = useMutation({
    mutationFn: (id) => workScheduleService.deleteWorkSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['work-schedules']);
      toast.success('Jornada de trabalho deletada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao deletar jornada de trabalho');
    },
  });

  const resetForm = () => {
    setFormData({
      user_id: '',
      selectedDays: [],
      start_time: '',
      end_time: '',
      break_start_time: '',
      break_end_time: '',
      is_active: true
    });
    setEditingSchedule(null);
    setShowForm(false);
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      user_id: schedule.user_id,
      selectedDays: [schedule.day_of_week], // Array com um único dia para edição
      start_time: schedule.start_time.substring(0, 5), // HH:MM
      end_time: schedule.end_time.substring(0, 5),
      break_start_time: schedule.break_start_time ? schedule.break_start_time.substring(0, 5) : '',
      break_end_time: schedule.break_end_time ? schedule.break_end_time.substring(0, 5) : '',
      is_active: schedule.is_active
    });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar se há dias selecionados
    if (!editingSchedule && formData.selectedDays.length === 0) {
      toast.error('Selecione pelo menos um dia da semana');
      return;
    }
    
    // Converter horários para formato HH:MM:SS
    const formatTime = (time) => {
      if (!time) return null;
      return time.length === 5 ? `${time}:00` : time;
    };

    if (editingSchedule) {
      // Edição: apenas um dia
      const payload = {
        user_id: parseInt(formData.user_id),
        day_of_week: parseInt(formData.selectedDays[0]),
        start_time: formatTime(formData.start_time),
        end_time: formatTime(formData.end_time),
        break_start_time: formData.break_start_time ? formatTime(formData.break_start_time) : null,
        break_end_time: formData.break_end_time ? formatTime(formData.break_end_time) : null,
        is_active: formData.is_active
      };
      updateMutation.mutate({ id: editingSchedule.id, data: payload });
    } else {
      // Criação: múltiplos dias
      const schedulesToCreate = formData.selectedDays.map(day => ({
        user_id: parseInt(formData.user_id),
        day_of_week: parseInt(day),
        start_time: formatTime(formData.start_time),
        end_time: formatTime(formData.end_time),
        break_start_time: formData.break_start_time ? formatTime(formData.break_start_time) : null,
        break_end_time: formData.break_end_time ? formatTime(formData.break_end_time) : null,
        is_active: formData.is_active
      }));
      createMutation.mutate(schedulesToCreate);
    }
  };

  // Funções para seleção rápida de dias
  const selectWeekdays = () => {
    setFormData({ ...formData, selectedDays: [1, 2, 3, 4, 5] }); // Segunda a Sexta
  };

  const selectWeekend = () => {
    setFormData({ ...formData, selectedDays: [0, 6] }); // Domingo e Sábado
  };

  const selectAllDays = () => {
    setFormData({ ...formData, selectedDays: [0, 1, 2, 3, 4, 5, 6] });
  };

  const clearDays = () => {
    setFormData({ ...formData, selectedDays: [] });
  };

  const toggleDay = (dayIndex) => {
    const currentDays = formData.selectedDays;
    if (currentDays.includes(dayIndex)) {
      setFormData({ ...formData, selectedDays: currentDays.filter(d => d !== dayIndex) });
    } else {
      setFormData({ ...formData, selectedDays: [...currentDays, dayIndex] });
    }
  };

  // Toggle para expandir/colapsar jornadas de um usuário
  const toggleUserExpanded = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Expandir todos os usuários
  const expandAll = () => {
    const allExpanded = {};
    Object.keys(schedulesByUser).forEach(userId => {
      allExpanded[userId] = true;
    });
    setExpandedUsers(allExpanded);
  };

  // Colapsar todos os usuários
  const collapseAll = () => {
    setExpandedUsers({});
  };

  const handleDelete = (id) => {
    setConfirmModal({
      show: true,
      message: 'Tem certeza que deseja deletar esta jornada de trabalho?',
      onConfirm: () => {
        deleteMutation.mutate(id);
        setConfirmModal({ show: false, onConfirm: null, message: '' });
      }
    });
  };

  const dayNames = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
  
  // Garantir que schedules seja sempre um array
  const schedules = Array.isArray(schedulesData?.data) 
    ? schedulesData.data 
    : Array.isArray(schedulesData?.data?.data)
    ? schedulesData.data.data
    : [];
  
  // Garantir que users seja sempre um array - o backend retorna { success: true, data: { users: [...], pagination: {...} } }
  const users = useMemo(() => {
    if (!usersData) return [];
    
    // Formato do backend: { data: { users: [...], pagination: {...} } }
    if (Array.isArray(usersData?.data?.users)) {
      return usersData.data.users;
    }
    
    // Formato alternativo: { data: { data: { users: [...] } } }
    if (Array.isArray(usersData?.data?.data?.users)) {
      return usersData.data.data.users;
    }
    
    // Formato alternativo: { data: [...] }
    if (Array.isArray(usersData?.data)) {
      return usersData.data;
    }
    
    // Se não for nenhum formato conhecido, retornar array vazio
    console.warn('Formato de dados de usuários não reconhecido:', usersData);
    return [];
  }, [usersData]);

  // Agrupar jornadas por funcionário
  const schedulesByUser = schedules.reduce((acc, schedule) => {
    if (!acc[schedule.user_id]) {
      acc[schedule.user_id] = {
        user: schedule.user_name,
        schedules: []
      };
    }
    acc[schedule.user_id].schedules.push(schedule);
    return acc;
  }, {});

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Jornadas de Trabalho</h1>
          <p className="text-gray-600">Gerencie os horários de trabalho dos funcionários</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                disabled={isLoadingUsers}
              >
                <option value="">
                  {isLoadingUsers ? 'Carregando...' : 'Todos os funcionários'}
                </option>
                {users.length > 0 && users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name}
                  </option>
                ))}
              </select>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="bg-[#49943d] text-white px-4 py-2 rounded-lg hover:bg-[#3a7630] flex items-center gap-2 justify-center"
          >
            <Plus className="w-4 h-4" />
            Nova Jornada
          </button>
        </div>
      </div>

      {/* Formulário */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              {editingSchedule ? 'Editar Jornada' : 'Nova Jornada'}
            </h2>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Funcionário *
                </label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoadingUsers}
                >
                  <option value="">
                    {isLoadingUsers ? 'Carregando funcionários...' : 'Selecione um funcionário'}
                  </option>
                  {users.length > 0 ? (
                    users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name}
                      </option>
                    ))
                  ) : !isLoadingUsers ? (
                    <option value="" disabled>Nenhum funcionário encontrado</option>
                  ) : null}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dias da Semana * {!editingSchedule && <span className="text-xs text-gray-500">(Selecione múltiplos dias)</span>}
                </label>
                
                {/* Botões rápidos (apenas na criação) */}
                {!editingSchedule && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      onClick={selectWeekdays}
                      className="px-3 py-1 text-xs bg-[#d9ead9] text-[#2c5824] rounded-lg hover:bg-[#b3d5b3] transition-colors"
                    >
                      Seg-Sex
                    </button>
                    <button
                      type="button"
                      onClick={selectWeekend}
                      className="px-3 py-1 text-xs bg-[#d9ead9] text-[#2c5824] rounded-lg hover:bg-[#b3d5b3] transition-colors"
                    >
                      Fim de Semana
                    </button>
                    <button
                      type="button"
                      onClick={selectAllDays}
                      className="px-3 py-1 text-xs bg-[#d9ead9] text-[#2c5824] rounded-lg hover:bg-[#b3d5b3] transition-colors"
                    >
                      Todos
                    </button>
                    <button
                      type="button"
                      onClick={clearDays}
                      className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Limpar
                    </button>
                  </div>
                )}

                {/* Checkboxes dos dias */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {dayNames.map((day, index) => (
                    <label
                      key={index}
                      className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                        formData.selectedDays.includes(index)
                          ? 'border-[#49943d] bg-[#f0f7f0]'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedDays.includes(index)}
                        onChange={() => toggleDay(index)}
                        disabled={editingSchedule && formData.selectedDays[0] !== index}
                        className="w-4 h-4 text-[#49943d] border-gray-300 rounded focus:ring-[#49943d] mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">{day}</span>
                    </label>
                  ))}
                </div>
                {!editingSchedule && formData.selectedDays.length === 0 && (
                  <p className="mt-2 text-xs text-red-600">Selecione pelo menos um dia</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Horário de Início *
                </label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Horário de Fim *
                </label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Início do Intervalo
                </label>
                <input
                  type="time"
                  value={formData.break_start_time}
                  onChange={(e) => setFormData({ ...formData, break_start_time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fim do Intervalo
                </label>
                <input
                  type="time"
                  value={formData.break_end_time}
                  onChange={(e) => setFormData({ ...formData, break_end_time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-[#49943d] border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Jornada ativa</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] flex items-center gap-2"
                disabled={createMutation.isLoading || updateMutation.isLoading}
              >
                <Save className="w-4 h-4" />
                {editingSchedule ? 'Atualizar' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Jornadas */}
      {isLoading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Carregando jornadas...</p>
        </div>
      ) : schedules.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Nenhuma jornada de trabalho cadastrada</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Botões de controle */}
          {Object.keys(schedulesByUser).length > 0 && (
            <div className="flex justify-end gap-2 mb-2">
              <button
                onClick={expandAll}
                className="px-3 py-1 text-xs text-[#49943d] hover:bg-[#f0f7f0] rounded-lg transition-colors"
              >
                Expandir Todos
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Colapsar Todos
              </button>
            </div>
          )}

          {/* Lista de usuários com dropdown */}
          {Object.entries(schedulesByUser).map(([userId, { user, schedules: userSchedules }]) => {
            const isExpanded = expandedUsers[userId];
            const sortedSchedules = userSchedules.sort((a, b) => a.day_of_week - b.day_of_week);
            const activeSchedules = sortedSchedules.filter(s => s.is_active);
            const inactiveSchedules = sortedSchedules.filter(s => !s.is_active);

            return (
              <div key={userId} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Header do dropdown */}
                <button
                  onClick={() => toggleUserExpanded(userId)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-[#49943d]" />
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">{user}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {sortedSchedules.length} jornada{sortedSchedules.length !== 1 ? 's' : ''} cadastrada{sortedSchedules.length !== 1 ? 's' : ''}
                        {inactiveSchedules.length > 0 && ` • ${inactiveSchedules.length} inativa${inactiveSchedules.length !== 1 ? 's' : ''}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {activeSchedules.length > 0 && (
                      <span className="px-2 py-1 text-xs bg-[#d4f0d9] text-[#4a6f29] rounded-full">
                        {activeSchedules.length} ativa{activeSchedules.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Conteúdo expandido */}
                {isExpanded && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {sortedSchedules.map((schedule) => (
                        <div
                          key={schedule.id}
                          className={`border rounded-lg p-3 transition-colors ${
                            schedule.is_active
                              ? 'border-gray-200 bg-white hover:bg-gray-50'
                              : 'border-gray-300 bg-gray-100 opacity-75'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-sm text-gray-900">
                                  {dayNames[schedule.day_of_week]}
                                </h4>
                                {!schedule.is_active && (
                                  <span className="px-1.5 py-0.5 text-xs bg-gray-300 text-gray-700 rounded">
                                    Inativa
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-gray-600 mt-1.5">
                                <Clock className="w-3.5 h-3.5" />
                                <span>
                                  {schedule.start_time.substring(0, 5)} - {schedule.end_time.substring(0, 5)}
                                </span>
                              </div>
                              {schedule.break_start_time && schedule.break_end_time && (
                                <div className="text-xs text-gray-500 mt-1 ml-5">
                                  Intervalo: {schedule.break_start_time.substring(0, 5)} - {schedule.break_end_time.substring(0, 5)}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <button
                                onClick={() => handleEdit(schedule)}
                                className="p-1.5 text-[#49943d] hover:text-[#2c5824] hover:bg-[#f0f7f0] rounded transition-colors"
                                title="Editar"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(schedule.id)}
                                className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                title="Deletar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false, onConfirm: null, message: '' })}
        title="Confirmar Exclusão"
        message={confirmModal.message}
        type="danger"
      />
    </div>
  );
};

export default WorkSchedulesManagement;

